import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiPrinter, FiFileText } = FiIcons;

/* ─── Gift display map ──────────────────────────────────────────────── */
const GIFT_MAP = {
  candle:  { emoji: '🕯️', label: 'Candle'  },
  flower:  { emoji: '🌹', label: 'Flower'  },
  dove:    { emoji: '🕊️', label: 'Dove'    },
  prayer:  { emoji: '🙏', label: 'Prayer'  },
  message: { emoji: '💬', label: 'Message' },
};

const giftDisplay = (gift, giftOption) => {
  const g = GIFT_MAP[gift] || { emoji: '🎁', label: gift || '—' };
  return giftOption ? `${g.emoji} ${g.label} (${giftOption})` : `${g.emoji} ${g.label}`;
};

const fmtDate = iso => {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso || '—'; }
};

/* ─── HTML report builder ───────────────────────────────────────────── */
const buildPrintHTML = (stats, gifts, tributes) => {
  const total = gifts.reduce((a, g) => a + g.count, 0);

  /* tribute rows */
  const tributeRows = tributes.map((t, i) => `
    <tr class="${i % 2 === 0 ? 'row-even' : 'row-odd'}">
      <td class="idx">${i + 1}</td>
      <td>
        <div class="author-name">${t.author || '—'}</div>
        <div class="author-rel">${t.relation || 'Not specified'}</div>
      </td>
      <td class="msg-cell"><span class="quote-mark">"</span>${t.message || '—'}<span class="quote-mark">"</span></td>
      <td><span class="gift-badge">${giftDisplay(t.gift || t.type, t.giftOption)}</span></td>
      <td class="date-cell">${fmtDate(t.date || t.submittedAt)}</td>
    </tr>`).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Guestbook Report — Memorial Sanctuary</title>
  <style>
    /* ── Reset & base ── */
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Georgia',serif;color:#1E3A5F;background:#fff;padding:48px 52px}
    a{color:inherit;text-decoration:none}

    /* ── Header ── */
    .report-header{display:flex;align-items:flex-start;justify-content:space-between;
      border-bottom:3px solid #C9A84C;padding-bottom:28px;margin-bottom:40px}
    .report-header h1{font-size:30px;letter-spacing:.03em;color:#1E3A5F}
    .report-header .sub{font-size:10px;text-transform:uppercase;letter-spacing:.14em;
      color:#7a8fa6;margin-top:6px}
    .report-header .date{font-size:10px;color:#7a8fa6;margin-top:4px}
    .brand-badge{font-size:9px;font-family:sans-serif;text-transform:uppercase;
      letter-spacing:.14em;color:#C9A84C;border:1px solid #C9A84C;
      padding:4px 10px;border-radius:20px;margin-top:8px;display:inline-block}

    /* ── Section headings ── */
    section{margin-bottom:44px}
    section h2{font-size:13px;text-transform:uppercase;letter-spacing:.12em;color:#C9A84C;
      margin-bottom:18px;border-bottom:1px solid #ede8df;padding-bottom:10px;
      display:flex;align-items:center;gap:8px}
    section h2::before{content:'';display:inline-block;width:4px;height:16px;
      background:#C9A84C;border-radius:2px;flex-shrink:0}

    /* ── Stats grid ── */
    .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
    .stat-card{border:1px solid #d6cfc4;border-radius:14px;padding:18px}
    .stat-card .val{font-size:26px;font-weight:700;color:#1E3A5F}
    .stat-card .lbl{font-size:9px;text-transform:uppercase;letter-spacing:.12em;
      color:#7a8fa6;margin-top:5px}
    .stat-card .sub{font-size:9px;color:#3a7d44;margin-top:7px;font-weight:700}

    /* ── Distribution table ── */
    .dist-table{width:100%;border-collapse:collapse;font-size:13px}
    .dist-table th{text-align:left;padding:10px 14px;background:#f5f0e8;
      font-size:9px;text-transform:uppercase;letter-spacing:.1em;color:#7a8fa6}
    .dist-table td{padding:10px 14px;border-bottom:1px solid #ede8df;color:#1E3A5F}
    .dist-table tr:last-child td{border-bottom:none}
    .bar-wrap{background:#f0ece4;border-radius:999px;height:8px;
      width:160px;display:inline-block;vertical-align:middle}
    .bar{background:#C9A84C;height:8px;border-radius:999px}

    /* ── Tribute log table ── */
    .tribute-table{width:100%;border-collapse:collapse;font-size:12px}
    .tribute-table th{text-align:left;padding:10px 12px;background:#1E3A5F;color:#fff;
      font-size:9px;text-transform:uppercase;letter-spacing:.1em;font-family:sans-serif}
    .tribute-table th:first-child{border-radius:10px 0 0 0}
    .tribute-table th:last-child{border-radius:0 10px 0 0}
    .tribute-table td{padding:12px;vertical-align:top;color:#1E3A5F;
      border-bottom:1px solid #ede8df}
    .row-even{background:#fff}
    .row-odd{background:#faf8f5}
    .tribute-table tr:last-child td{border-bottom:none}
    .idx{width:32px;text-align:center;font-size:10px;color:#aaa;font-family:sans-serif}
    .author-name{font-weight:700;font-size:13px;color:#1E3A5F}
    .author-rel{font-size:9px;text-transform:uppercase;letter-spacing:.1em;
      color:#7a8fa6;margin-top:3px;font-family:sans-serif}
    .msg-cell{max-width:320px;font-style:italic;font-size:12px;
      line-height:1.6;color:#3a4f6e}
    .quote-mark{color:#C9A84C;font-size:16px;line-height:0;vertical-align:-3px}
    .gift-badge{display:inline-block;font-size:10px;padding:3px 9px;
      border-radius:20px;background:#f5f0e8;border:1px solid #e0d8cc;
      color:#1E3A5F;white-space:nowrap;font-family:sans-serif}
    .date-cell{font-size:10px;color:#7a8fa6;white-space:nowrap;font-family:sans-serif}

    /* ── Empty state ── */
    .no-tributes{text-align:center;padding:40px;color:#7a8fa6;font-style:italic;
      background:#faf8f5;border-radius:14px;border:1px dashed #d6cfc4}

    /* ── Summary callout ── */
    .summary-box{background:#1E3A5F;border-radius:16px;padding:24px;
      display:flex;gap:40px;flex-wrap:wrap}
    .summary-box .s-item{color:#fff}
    .summary-box .s-val{font-size:22px;font-weight:700}
    .summary-box .s-lbl{font-size:9px;text-transform:uppercase;letter-spacing:.12em;
      color:rgba(255,255,255,.45);margin-top:4px;font-family:sans-serif}

    /* ── Footer ── */
    footer{margin-top:52px;border-top:1px solid #e8e0d0;padding-top:18px;
      font-size:9px;color:#7a8fa6;display:flex;justify-content:space-between;
      letter-spacing:.08em;font-family:sans-serif}

    @media print{
      body{padding:28px 32px}
      .tribute-table th{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#1E3A5F!important}
      .stat-card,.summary-box,.row-odd{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    }
  </style>
</head>
<body>

  <!-- ── Report header ── -->
  <div class="report-header">
    <div>
      <h1>Guestbook Analytics Report</h1>
      <div class="sub">Memorial Sanctuary Platform</div>
      <div class="date">Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
    </div>
    <div style="text-align:right">
      <div class="brand-badge">Memorial Sanctuary</div>
      <div class="date" style="margin-top:10px">Confidential Document</div>
    </div>
  </div>

  <!-- ── Key metrics ── -->
  <section>
    <h2>Key Metrics</h2>
    <div class="stats-grid">
      ${stats.map(s => `
        <div class="stat-card">
          <div class="val">${s.value}</div>
          <div class="lbl">${s.label}</div>
          <div class="sub">${s.sub}</div>
        </div>`).join('')}
    </div>
  </section>

  <!-- ── Interaction distribution ── -->
  <section>
    <h2>Interaction Distribution</h2>
    <table class="dist-table">
      <thead>
        <tr>
          <th>Type</th><th>Count</th><th>Share</th><th>Visual</th>
        </tr>
      </thead>
      <tbody>
        ${gifts.map(g => {
          const pct = ((g.count / total) * 100).toFixed(1);
          return `<tr>
            <td>${g.label}</td>
            <td><strong>${g.count}</strong></td>
            <td>${pct}%</td>
            <td>
              <span class="bar-wrap">
                <span class="bar" style="width:${pct}%"></span>
              </span>
            </td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
  </section>

  <!-- ── Tribute log ── -->
  <section>
    <h2>Tribute Log (${tributes.length} ${tributes.length === 1 ? 'entry' : 'entries'})</h2>

    ${tributes.length === 0
      ? `<div class="no-tributes">No tribute records available for export.</div>`
      : `<table class="tribute-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Author &amp; Relationship</th>
              <th>Message</th>
              <th>Gift / Interaction</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>${tributeRows}</tbody>
        </table>`
    }
  </section>

  <!-- ── Summary callout ── -->
  <section>
    <h2>Export Summary</h2>
    <div class="summary-box">
      <div class="s-item">
        <div class="s-val">${tributes.length}</div>
        <div class="s-lbl">Tributes Exported</div>
      </div>
      <div class="s-item">
        <div class="s-val">${total.toLocaleString()}</div>
        <div class="s-lbl">Total Interactions</div>
      </div>
      <div class="s-item">
        <div class="s-val">${gifts.length}</div>
        <div class="s-lbl">Gift Types</div>
      </div>
      <div class="s-item">
        <div class="s-val">${new Date().getFullYear()}</div>
        <div class="s-lbl">Report Year</div>
      </div>
    </div>
  </section>

  <footer>
    <span>Memorial Sanctuary &nbsp;·&nbsp; Guestbook Analytics Report</span>
    <span>Confidential &nbsp;·&nbsp; Do not distribute without authorisation</span>
  </footer>

</body>
</html>`;
};

/* ─── Modal component ───────────────────────────────────────────────── */
export default function GuestbookExportModal({ open, onClose, stats, gifts, tributes = [] }) {

  const openPreviewWindow = () => {
    const html = buildPrintHTML(stats, gifts, tributes);
    const win  = window.open('', '_blank', 'width=1000,height=760');
    win.document.write(html);
    win.document.close();
    return win;
  };

  const handlePrint = () => {
    const win = openPreviewWindow();
    setTimeout(() => win.print(), 450);
    onClose();
  };

  const handlePDF = () => {
    const html = buildPrintHTML(stats, gifts, tributes);
    const blob = new Blob([html], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, '_blank', 'width=1000,height=760');
    setTimeout(() => { win.print(); }, 650);
    URL.revokeObjectURL(url);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Card */}
          <motion.div
            className="relative z-10 bg-white rounded-[32px] shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1,    y: 0,  opacity: 1 }}
            exit={{    scale: 0.92, y: 24, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-8 pt-8 pb-4">
              <div>
                <h3 className="text-lg font-serif text-accent-navy">Export Guestbook Data</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-0.5">
                  Choose export format
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-brand-light flex items-center justify-center hover:bg-red-50 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 text-text-muted" />
              </button>
            </div>

            {/* Tribute count badge */}
            {tributes.length > 0 && (
              <div className="mx-8 mb-4 px-4 py-3 bg-brand-light rounded-2xl border border-brand-dark flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-accent-navy flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiFileText} className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Report Includes</p>
                  <p className="text-xs font-bold text-accent-navy">
                    {tributes.length} tribute {tributes.length === 1 ? 'entry' : 'entries'} with full details
                  </p>
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="mx-8 border-t border-brand-dark" />

            {/* Format options */}
            <div className="px-8 py-8 grid grid-cols-2 gap-4">
              {/* PDF */}
              <motion.button
                onClick={handlePDF}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="flex flex-col items-center gap-4 p-6 rounded-2xl border-2 border-brand-dark hover:border-accent-gold bg-brand-light transition-all group"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow"
                  style={{ background: 'linear-gradient(135deg,#e53e3e,#c53030)' }}
                >
                  <SafeIcon icon={FiFileText} className="w-7 h-7 text-white" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-black text-accent-navy uppercase tracking-widest">PDF</p>
                  <p className="text-[9px] text-text-muted mt-1 leading-relaxed">
                    Save as a portable<br />document file
                  </p>
                </div>
              </motion.button>

              {/* Print */}
              <motion.button
                onClick={handlePrint}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="flex flex-col items-center gap-4 p-6 rounded-2xl border-2 border-brand-dark hover:border-accent-gold bg-brand-light transition-all group"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow"
                  style={{ background: 'linear-gradient(135deg,#1E3A5F,#2a4f7c)' }}
                >
                  <SafeIcon icon={FiPrinter} className="w-7 h-7 text-white" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-black text-accent-navy uppercase tracking-widest">Print</p>
                  <p className="text-[9px] text-text-muted mt-1 leading-relaxed">
                    Send to printer or<br />print to PDF
                  </p>
                </div>
              </motion.button>
            </div>

            {/* Footer hint */}
            <div className="px-8 pb-8">
              <p className="text-[9px] text-center text-text-muted leading-relaxed">
                A formatted report with full tribute details opens in a new window.<br />
                For PDF, choose <strong>"Save as PDF"</strong> in the print dialog.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}